import React, { useState, useEffect } from 'react';
import { IconButton, makeStyles, Typography } from '@material-ui/core';
import MUISnackbar from '@material-ui/core/Snackbar';
import CloseIcon from '@material-ui/icons/Close';
import WarningIcon from '@material-ui/icons/Warning';

const FOLLOW_UP_RING = '0 0 0 3px rgba(255,152,0,0.5)';

const useStyles = makeStyles(() => ({
  container: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    // Same width as ConsentNotification / the shared Snackbar — long
    // multi-patient lists simply wrap onto extra lines inside it. The amber
    // fill is deliberate: the other notifications are white cards, so a
    // clinical warning must not look like one of them.
    width: 'min(400px, calc(100vw - 32px))',
    minHeight: '50px',
    background: '#FFF4E5',
    color: '#663C00',
    padding: '0.75em 0.9em',
    borderRadius: '4px',
    border: '1px solid #FFB74D',
    borderLeft: '6px solid #F57C00',
    // A ring rather than a drop shadow — dark shadows are invisible over the
    // black video background.
    boxShadow: FOLLOW_UP_RING,
    fontSize: '0.85rem',
    // Pulse on entry only; a looping animation would distract mid-consult.
    animation: '$followUpPulse 1.1s ease-out 3',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
  '@keyframes followUpPulse': {
    '0%': {
      boxShadow: `${FOLLOW_UP_RING}, 0 0 0 0 rgba(255,167,38,0.95)`,
    },
    '100%': {
      boxShadow: `${FOLLOW_UP_RING}, 0 0 0 14px rgba(255,167,38,0)`,
    },
  },
  closeButton: {
    position: 'absolute',
    top: 2,
    right: 2,
    color: '#8A5A1A',
  },
  contentContainer: {
    display: 'flex',
    lineHeight: 1.35,
  },
  iconContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 'none',
    width: 32,
    height: 32,
    marginRight: '0.7em',
    borderRadius: '50%',
    background: '#D32F2F',
  },
  warningIcon: {
    color: 'white',
    fontSize: '1.2rem',
    // The triangle's visual centre sits below its box centre
    transform: 'translateY(-1px)',
  },
  headline: {
    // The theme's Typography colour would otherwise override the container's
    color: 'inherit',
    fontWeight: 'bold',
    fontSize: '1rem',
    marginBottom: '0.2em',
  },
  messageLine: {
    color: 'inherit',
    fontSize: '0.8rem',
    marginTop: '0.1em',
    lineHeight: 1.3,
  },
}));

function isFollowUpDoctorSession(): boolean {
  const searchStr = window.sessionStorage.getItem('searchStr');
  if (!searchStr) return false;
  const params = new URLSearchParams(searchStr);
  return params.get('isAgentCall') === 'true' && params.get('followUp') === 'true';
}

function getFollowUpNames(): string[] {
  const searchStr = window.sessionStorage.getItem('searchStr');
  if (!searchStr) return [];
  const params = new URLSearchParams(searchStr);
  return (params.get('followUpNames') || '')
    .split(',')
    .map(name => name.trim())
    .filter(Boolean);
}

function formatNameList(names: string[]): string {
  if (names.length <= 1) return names[0] || '';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

export default function FollowUpNotification() {
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (isFollowUpDoctorSession()) {
      setOpen(true);
    }
  }, []);

  const names = getFollowUpNames();
  const isPlural = names.length > 1;
  // Older plugin builds send followUp=true without names — keep a generic line
  const message = names.length ? (
    <>
      <strong>{formatNameList(names)}</strong> {isPlural ? 'are follow-up patients' : 'is a follow-up patient'}.
    </>
  ) : (
    'This patient attended a consultation within the last 24 hours.'
  );

  return (
    <MUISnackbar
      // Top-CENTER: the top-right column is fully claimed (the shared
      // Snackbar used by RecordingNotifications sits at top: 128 and
      // ConsentNotification at top: 240, and MUI snackbars overlap rather
      // than stack). Nothing renders top-center, and a clinical warning is
      // hardest to miss there.
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      style={{ top: 24 }}
      open={open}
      autoHideDuration={null}
    >
      <div className={classes.container}>
        <IconButton size="small" aria-label="close" className={classes.closeButton} onClick={() => setOpen(false)}>
          <CloseIcon fontSize="small" />
        </IconButton>
        <div className={classes.contentContainer}>
          <div className={classes.iconContainer}>
            <WarningIcon className={classes.warningIcon} />
          </div>
          <div>
            <Typography variant="body1" className={classes.headline} component="div">
              {isPlural ? 'Follow-up Patients' : 'Follow-up Patient'}
            </Typography>
            <Typography variant="body1" component="div" className={classes.messageLine}>
              {message}
            </Typography>
            <Typography variant="body1" component="div" className={classes.messageLine}>
              Please read notes and check any results
            </Typography>
          </div>
        </div>
      </div>
    </MUISnackbar>
  );
}
